import { useCallback, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { useSelector } from "react-redux"
import {
  ArrowUpDown,
  Check,
  ChevronDown,
  Download,
  RefreshCw,
  Search,
  X,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table"
import { cn } from "@/lib/utils"
import { RootState } from "@/reducers/store"
import { DateRangePicker } from "@/components/ui/DateRangePicker"
import { LicensePlate } from "@/components/ui/license-plate"
import ImageGallery from "@/components/photoView/ImageGallery"
import PDFViewer from "@/components/pdfView/PDFViewer"
import useHandleDownload from "@/hooks/useHandleDownload"
import { mapFileUrlToSameOrigin } from "@/components/message/utils/mapFileToSameOrigin"
import { useCmrSearch } from "./useCmrSearch"
import { useCmrDocumentPreview, PreviewImageDoc } from "./useCmrDocumentPreview"
import { Thumbnail, isPDF, formatTime24h, formatDateShort } from "./Thumbnail"
import { resolveDriverDisplayName } from "./resolveDriverDisplayName"
import { resolveSearchDriverName } from "./resolveSearchDriverName"
import type { CmrDocumentMetadata, UnlinkedDocument } from "./useCmrReportData"
import { useTranslation } from "react-i18next"

function sanitizeFilenamePart(value: string): string {
  return value.replace(/[^A-Za-z0-9._-]+/g, "_").replace(/^_+|_+$/g, "")
}

function getExtensionFromUrl(url: string, fallback = "bin"): string {
  const last = url.split(/[?#]/)[0].split("/").pop() || ""
  const dot = last.lastIndexOf(".")
  if (dot < 0) return fallback
  const ext = last.slice(dot + 1)
  return /^[A-Za-z0-9]{1,6}$/.test(ext) ? ext : fallback
}

function buildDownloadFileName(args: {
  url: string
  documentType?: string
  plates?: string
  timestamp?: string
}): string {
  const ext = getExtensionFromUrl(args.url)
  const parts: string[] = []
  if (args.documentType) parts.push(sanitizeFilenamePart(args.documentType))
  if (args.plates) parts.push(sanitizeFilenamePart(args.plates))
  if (args.timestamp) {
    const d = new Date(args.timestamp)
    if (!Number.isNaN(d.getTime())) {
      const yyyy = d.getFullYear()
      const mm = String(d.getMonth() + 1).padStart(2, "0")
      const dd = String(d.getDate()).padStart(2, "0")
      const hh = String(d.getHours()).padStart(2, "0")
      const mi = String(d.getMinutes()).padStart(2, "0")
      parts.push(`${yyyy}-${mm}-${dd}_${hh}${mi}`)
    }
  }
  const stem = parts.length > 0 ? parts.join("_") : "document"
  return `${stem}.${ext}`
}

type DriverOption = {
  jid: string
  displayName: string
  chatJid: string
}

function useDriverOptions(): DriverOption[] {
  const chats = useSelector((state: RootState) => state.chats)
  const profiles = useSelector((state: RootState) => state.profiles)
  const accountChats = useSelector((state: RootState) => state.accountChats)

  return useMemo(() => {
    const seen = new Map<string, DriverOption>()
    for (const chat of chats) {
      const driverAJid = accountChats[chat.jid]?.driverA
      const candidateJid =
        driverAJid || chat.participants?.find((p) => p.role === "driver")?.jid
      if (!candidateJid) continue
      if (seen.has(candidateJid)) continue
      seen.set(candidateJid, {
        jid: candidateJid,
        chatJid: chat.jid,
        displayName: resolveDriverDisplayName({
          chatJid: chat.jid,
          driverJid: candidateJid,
          chats,
          profiles,
          accountChats,
        }),
      })
    }
    return Array.from(seen.values()).sort((a, b) =>
      a.displayName.localeCompare(b.displayName),
    )
  }, [chats, profiles, accountChats])
}

function DriverPicker({
  value,
  onChange,
}: {
  value?: string
  onChange: (next?: string) => void
}) {
  const { t } = useTranslation()
  const options = useDriverOptions()
  const [open, setOpen] = useState(false)
  const selected = options.find((o) => o.jid === value)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="flex items-center justify-between rounded-md border px-3 py-2 text-left text-sm hover:bg-accent">
          <span className="truncate">
            {selected ? selected.displayName : t("reports.cmrSearch.anyDriver")}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="z-[1001] w-[300px] p-0">
        <Command>
          <CommandInput placeholder={t("reports.cmrSearch.searchDrivers")} />
          <CommandList>
            <CommandEmpty>{t("reports.shared.noDriverFound")}</CommandEmpty>
            <CommandGroup>
              <CommandItem
                onSelect={() => {
                  onChange(undefined)
                  setOpen(false)
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    !value ? "opacity-100" : "opacity-0",
                  )}
                />
                {t("reports.cmrSearch.anyDriver")}
              </CommandItem>
              {options.map((opt) => (
                <CommandItem
                  key={opt.jid}
                  value={`${opt.displayName} ${opt.jid}`}
                  onSelect={() => {
                    onChange(opt.jid)
                    setOpen(false)
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === opt.jid ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {opt.displayName}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

type Row = UnlinkedDocument & {
  driverDisplayName: string
  roomDisplayName: string
}

export function CmrSearchDialog({
  open,
  onClose,
  onNavigateToChat,
}: {
  open: boolean
  onClose: () => void
  onNavigateToChat: (jid: string) => void
}) {
  const { t } = useTranslation()
  const defaultRange = useMemo(() => {
    const start = new Date()
    start.setHours(0, 0, 0, 0)
    const end = new Date()
    end.setHours(23, 59, 59, 999)
    return { from: start, to: end }
  }, [])

  const [dateRange, setDateRange] = useState<{ from: Date; to: Date } | null>(
    defaultRange,
  )
  const [plates, setPlates] = useState("")
  const [driverJid, setDriverJid] = useState<string | undefined>()
  const [freeText, setFreeText] = useState("")
  const [sorting, setSorting] = useState<SortingState>([
    { id: "timestamp", desc: true },
  ])

  const search = useCmrSearch()
  const preview = useCmrDocumentPreview()
  const downloadFile = useHandleDownload()

  const chats = useSelector((state: RootState) => state.chats)
  const profiles = useSelector((state: RootState) => state.profiles)
  const accountChats = useSelector((state: RootState) => state.accountChats)

  const handleSearch = useCallback(() => {
    if (!dateRange) return
    const fromStart = new Date(dateRange.from)
    fromStart.setHours(0, 0, 0, 0)
    const toEnd = new Date(dateRange.to)
    toEnd.setHours(23, 59, 59, 999)
    search.runSearch({
      dateStart: fromStart.toISOString(),
      dateEnd: toEnd.toISOString(),
      driverJid,
      roomJid: undefined,
      plates,
      freeText,
    })
  }, [search, dateRange, driverJid, plates, freeText])

  const handleReset = useCallback(() => {
    setDateRange(defaultRange)
    setPlates("")
    setDriverJid(undefined)
    setFreeText("")
    search.reset()
  }, [search, defaultRange])

  const rows = useMemo<Row[]>(() => {
    return search.results.map((doc) => {
      const driverDisplayName = resolveSearchDriverName({
        chatJid: doc.chatJid,
        driverJid: doc.driverJid,
        authorJid: doc.author,
        chats,
        profiles,
        accountChats,
      })
      const plates = doc.driverContext?.plates
      const roomDisplayName = [plates, driverDisplayName]
        .filter(Boolean)
        .join(" ")
      return {
        ...doc,
        driverDisplayName,
        roomDisplayName: roomDisplayName || "—",
      }
    })
  }, [search.results, chats, profiles, accountChats])

  const columns = useMemo<ColumnDef<Row>[]>(
    () => [
      {
        id: "thumbnail",
        header: "",
        enableSorting: false,
        cell: ({ row }) => {
          const url = row.original.fileUrl
          if (!url) return <span className="text-muted-foreground">—</span>
          const allImageDocs: PreviewImageDoc[] = rows
            .filter((r) => r.fileUrl && !isPDF(r.fileUrl))
            .map((r) => ({
              url: r.fileUrl!,
              metadata: {
                extractedMetadata: r.extractedMetadata,
                driverContext: r.driverContext,
              } as CmrDocumentMetadata,
              messageId: r.messageId,
              chatJid: r.chatJid,
              driverJid: r.driverJid,
            }))
          const downloadName = buildDownloadFileName({
            url,
            documentType: row.original.extractedMetadata?.documentType,
            plates: row.original.driverContext?.plates,
            timestamp: row.original.timestamp,
          })
          return (
            <div className="flex items-center gap-2">
              <Thumbnail
                url={url}
                fileName={
                  row.original.extractedMetadata?.documentType ||
                  row.original.messageId
                }
                onClick={() => {
                  if (isPDF(url)) {
                    preview.openPdf(
                      url,
                      {
                        extractedMetadata: row.original.extractedMetadata,
                        driverContext: row.original.driverContext,
                      } as CmrDocumentMetadata,
                      {
                        messageId: row.original.messageId,
                        chatJid: row.original.chatJid,
                        driverJid: row.original.driverJid,
                      },
                    )
                  } else {
                    preview.openImage(url, allImageDocs)
                  }
                }}
              />
              <Button
                variant="ghost"
                size="icon-sm"
                title={t("reports.cmrSearch.download", { name: downloadName })}
                onClick={(e) => {
                  e.stopPropagation()
                  downloadFile(mapFileUrlToSameOrigin(url), downloadName)
                }}
              >
                <Download className="h-4 w-4" />
              </Button>
            </div>
          )
        },
      },
      {
        accessorFn: (row) => row.extractedMetadata?.documentType || "",
        id: "documentType",
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("reports.cmrSearch.columns.documentType")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => row.original.extractedMetadata?.documentType || "—",
      },
      {
        accessorKey: "driverDisplayName",
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("reports.cmrSearch.columns.driver")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const knownChat =
            row.original.chatJid &&
            chats.some((c) => c.jid === row.original.chatJid)
          if (knownChat) {
            return (
              <button
                className="text-left font-medium text-blue-600 hover:underline"
                onClick={() => onNavigateToChat(row.original.chatJid!)}
              >
                {row.original.driverDisplayName}
              </button>
            )
          }
          return (
            <span className="font-medium">
              {row.original.driverDisplayName}
            </span>
          )
        },
      },
      {
        accessorFn: (row) => row.driverContext?.plates || "",
        id: "plates",
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("reports.cmrSearch.columns.plates")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const plates = row.original.driverContext?.plates
          if (!plates) return <span className="text-muted-foreground">—</span>
          return <LicensePlate plateNumber={plates} />
        },
      },
      {
        accessorKey: "roomDisplayName",
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("reports.cmrSearch.columns.driverChat")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const knownChat =
            row.original.chatJid &&
            chats.some((c) => c.jid === row.original.chatJid)
          if (knownChat) {
            return (
              <button
                className="text-left text-blue-600 hover:underline"
                onClick={() => onNavigateToChat(row.original.chatJid!)}
              >
                {row.original.roomDisplayName}
              </button>
            )
          }
          return <span>{row.original.roomDisplayName}</span>
        },
      },
      {
        accessorKey: "timestamp",
        header: ({ column }) => (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("reports.cmrSearch.columns.timestamp")}
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        ),
        cell: ({ row }) => {
          const timestamp = row.original.timestamp
          return (
            <span className="text-xs text-muted-foreground">
              {formatDateShort(timestamp)} {formatTime24h(timestamp)}
            </span>
          )
        },
      },
    ],
    [rows, preview, chats, onNavigateToChat, downloadFile, t],
  )

  const table = useReactTable<Row>({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  if (!open) return null

  return (
    <>
      {createPortal(
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50">
          <div className="relative flex h-[85vh] w-[90vw] max-w-5xl flex-col rounded-lg bg-background shadow-xl">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h2 className="text-lg font-semibold">
                {t("reports.cmrSearch.title")}
              </h2>
              <Button variant="ghost" size="icon-sm" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex-1 overflow-auto p-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                <DateRangePicker
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  maxDays={366}
                  popoverClassName="z-[1001]"
                  showClearButton
                />

                <Input
                  placeholder={t("reports.cmrSearch.platesPlaceholder")}
                  value={plates}
                  onChange={(e) => setPlates(e.target.value)}
                />

                <DriverPicker value={driverJid} onChange={setDriverJid} />

                <Input
                  placeholder={t("reports.cmrSearch.freeTextPlaceholder")}
                  value={freeText}
                  onChange={(e) => setFreeText(e.target.value)}
                />
              </div>

              <div className="mt-3 flex items-center justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={handleReset}
                  disabled={search.isLoading}
                >
                  {t("reports.cmrSearch.reset")}
                </Button>
                <Button
                  onClick={handleSearch}
                  disabled={search.isLoading || !dateRange}
                  className="bg-blue-600 text-white hover:bg-blue-700"
                >
                  {search.isLoading ? (
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="mr-2 h-4 w-4" />
                  )}
                  {t("common.search")}
                </Button>
              </div>

              {search.error && (
                <div className="mt-3 text-sm text-red-500">
                  {t("reports.shared.error", { error: search.error })}
                </div>
              )}

              {search.hitMaxCap && (
                <div className="mt-3 text-sm text-yellow-700 dark:text-yellow-300">
                  {t("reports.cmrSearch.maxCap")}
                </div>
              )}

              <div className="mt-4 rounded-md border">
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map((hg) => (
                      <TableRow key={hg.id}>
                        {hg.headers.map((h) => (
                          <TableHead key={h.id}>
                            {h.isPlaceholder
                              ? null
                              : flexRender(
                                  h.column.columnDef.header,
                                  h.getContext(),
                                )}
                          </TableHead>
                        ))}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {!search.hasSearched ? (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          className="h-24 text-center text-muted-foreground"
                        >
                          {t("reports.cmrSearch.notSearched")}
                        </TableCell>
                      </TableRow>
                    ) : search.isLoading ? (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          className="h-24 text-center text-muted-foreground"
                        >
                          {t("reports.cmrSearch.searching")}
                        </TableCell>
                      </TableRow>
                    ) : table.getRowModel().rows.length ? (
                      table.getRowModel().rows.map((row) => (
                        <TableRow key={row.id}>
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id}>
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={columns.length}
                          className="h-24 text-center"
                        >
                          {t("reports.cmrSearch.noResults")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
      {preview.galleryVisible && preview.ownsGallery && (
        <ImageGallery {...preview.galleryProps} onLinked={handleSearch} />
      )}
      {preview.pdfVisible && preview.ownsPdf && (
        <PDFViewer {...preview.pdfProps} onLinked={handleSearch} />
      )}
    </>
  )
}
